import { useState, useCallback } from 'react';
import { FOLLOW_ACTION, UNFOLLOW_ACTION, type FollowAction } from '@/src/listings/components/_shared/buttons/FollowButton/types';
import type { OrganizerFollowMutationResponse } from '@/src/listings/types/organizerFollow';
import { releaseFetchWithOverrideRecovery } from '@/lib/release/pageSourceOverride';

export interface FollowActionResult {
  success: boolean;
  numFollowers?: number;
}

export interface UseFollowActionsResult {
  follow: (organizerId: string) => Promise<FollowActionResult>;
  unfollow: (organizerId: string) => Promise<FollowActionResult>;
  followActionLoading: boolean;
}

export const useFollowActions = (): UseFollowActionsResult => {
  const [loading, setLoading] = useState(false);

  const performFollowAction = useCallback(
    async (organizerId: string, action: FollowAction): Promise<FollowActionResult> => {
      if (!organizerId) {
        console.error(`Failed to ${action} organizer: Organizer ID is required`);
        return { success: false };
      }

      try {
        setLoading(true);
        const response = await releaseFetchWithOverrideRecovery(`/e/api/organizer/${encodeURIComponent(organizerId)}/${action}`, {
          method: 'POST',
        });
        const body = (await response.json()) as OrganizerFollowMutationResponse;

        return {
          success: response.ok && body.actionPerformed,
          numFollowers: body.numFollowers,
        };
      } catch (error) {
        console.error(`Error ${action}ing organizer:`, error);
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const follow = useCallback(
    (organizerId: string) => performFollowAction(organizerId, FOLLOW_ACTION),
    [performFollowAction],
  );

  const unfollow = useCallback(
    (organizerId: string) => performFollowAction(organizerId, UNFOLLOW_ACTION),
    [performFollowAction],
  );

  return {
    follow,
    unfollow,
    followActionLoading: loading,
  };
};
